import {
	isContractor,
	isSupplierPayingCustomer,
	nullSafeGet,
	nullSafeGetOrElse,
	recordHasContractorId,
} from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	invoicesRestCrudThunksForBuyer,
	invoicesRestCrudThunksForSupplier,
	invoicesSubcontractorRestCrudThunks,
	invoicesContractorRestCrudThunks,
} from '../../thunks/invoices_thunks';
import { getSanitisedInvoice, getSanitisedQuote } from '../../utils/WorkOrderUtils';
import { isBuyerOrInternalTech } from '../../utils/AuthUtils';
import { SupplierFacilityName } from '../name_component/supplierFacility/supplierFacilityNameComponent';
import { BuyerCompanyName } from '../name_component/buyerCompany/buyerCompanyNameComponent';
import React from 'react';
import { isClientQuoteView } from '../quote_utils/QuoteUtils';

export const isClientInvoiceViewLocation = (location) =>
	!nullSafeGetOrElse('pathname', location, '').includes('subcontractor');
export const isSubcontractorInvoiceViewLocation = (location) =>
	nullSafeGetOrElse('pathname', location, '').includes('subcontractor');

export const getInvoiceSubUrl = (userType, currentUser, record) => {
	const hasContractorId = recordHasContractorId(record);
	const isClientView = !isContractor(currentUser, record);
	return userType === ROLE_TYPES.SUPPLIER
		? hasContractorId
			? isClientView
				? 'supplier/invoices/contractor'
				: 'supplier/invoices/subcontractor'
			: 'supplier/invoices'
		: 'buyer/invoices';
};

export const getInvoicePathName = (userType, currentUser, record) => {
	const hasContractorId = recordHasContractorId(record);
	const isClientView = !isContractor(currentUser, record);
	return userType === ROLE_TYPES.SUPPLIER
		? hasContractorId
			? isClientView && isSupplierPayingCustomer(currentUser)
				? 'supplier/invoices/contractor'
				: 'supplier/invoices/subcontractor'
			: 'supplier/invoices'
		: 'buyer/invoices';
};

export const getInvoiceSubAPIURL = (userType, currentUser, record) => {
	const hasContractorId = recordHasContractorId(record);
	const isClientView = !isContractor(currentUser, record);
	return userType === ROLE_TYPES.SUPPLIER
		? hasContractorId
			? isClientView
				? 'supplier/invoice/contractor_invoices'
				: 'supplier/invoice/subcontractor_invoices'
			: 'supplier/invoice/invoices'
		: 'buyer/invoice/invoices';
};

export const getInvoiceRestCrudThunksFromPath = (userType, location) =>
	userType === ROLE_TYPES.BUYER
		? invoicesRestCrudThunksForBuyer
		: nullSafeGetOrElse('pathname', location, '').includes('supplier/invoices/contractor')
		? invoicesContractorRestCrudThunks
		: nullSafeGetOrElse('pathname', location, '').includes('supplier/invoices/subcontractor')
		? invoicesSubcontractorRestCrudThunks
		: invoicesRestCrudThunksForSupplier;

export const getInvoiceRestCrudThunksContractorId = (
	userType,
	hasContractorId,
	isClientView = false
) =>
	userType === ROLE_TYPES.SUPPLIER
		? hasContractorId
			? isClientView
				? invoicesContractorRestCrudThunks
				: invoicesSubcontractorRestCrudThunks
			: invoicesRestCrudThunksForSupplier
		: invoicesRestCrudThunksForBuyer;

export const isClientInvoiceView = (invoice, currentUser) => {
	if (invoice.subSupplierFacilityId === undefined) return true;
	return invoice.subSupplierFacilityId === nullSafeGet('facility.id', currentUser);
};

export const getInvoiceOtherCompanyEntity = (invoiceItem, currentUser) => {
	const invoice = getSanitisedInvoice(invoiceItem);
	const isContractorWorkOrder = recordHasContractorId(invoice);
	if (isBuyerOrInternalTech(currentUser)) {
		return nullSafeGet('supplierFacility', invoice);
	} else if (isContractorWorkOrder) {
		if (isClientInvoiceView(invoice, currentUser)) {
			return nullSafeGet('contractorSupplierFacility', invoice);
		} else {
			return nullSafeGet('subSupplierFacility', invoice);
		}
	} else {
		return nullSafeGet('workOrder.buyerCompany', invoice);
	}
};

export const getInvoiceOtherCompany = (invoiceItem, currentUser) => {
	const invoice = getSanitisedInvoice(invoiceItem);
	const isContractorWorkOrder = recordHasContractorId(invoice);
	if (isBuyerOrInternalTech(currentUser)) {
		//Return normal third party supplier
		return (
			<SupplierFacilityName
				facility={nullSafeGet('supplierFacility', invoice)}
				mode={'card'}
				placement={'top'}
				allowHover={false}
			/>
		);
	} else if (isContractorWorkOrder) {
		if (isClientQuoteView(invoice, currentUser)) {
			return (
				<SupplierFacilityName
					facility={nullSafeGet('contractorSupplierFacility', invoice)}
					mode={'card'}
					placement={'top'}
					allowHover={false}
				/>
			);
		} else {
			return (
				<SupplierFacilityName
					facility={nullSafeGet('subSupplierFacility', invoice)}
					mode={'card'}
					placement={'top'}
					allowHover={false}
				/>
			);
		}
	} else {
		return (
			<BuyerCompanyName
				company={nullSafeGet('workOrder.buyerCompany', invoice)}
				mode={'card'}
				placement={'top'}
				allowHover={false}
			/>
		);
	}
};

export const getInvoiceMissingProperties = (invoice) => {
	const missingInvoiceNumber = invoice.invoiceNumber === undefined;
	const missingDateOfInvoice = invoice.dateOfInvoice === undefined;
	const missingPDFLink = nullSafeGetOrElse('invoicePDFLink', invoice, []).length === 0;
	const missingAProperty = missingPDFLink || missingDateOfInvoice || missingInvoiceNumber;
	return { missingInvoiceNumber, missingDateOfInvoice, missingPDFLink, missingAProperty };
};

export const formatRevisionInfo = (revisionInfo) => {
	return nullSafeGet('changes', revisionInfo)
		? revisionInfo.changes.reduce((acc, { fieldName, newValue }) => {
				let val = newValue;
				if (typeof newValue === 'string') {
					const trimmed = newValue.trim();
					if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
						try {
							val = JSON.parse(trimmed);
						} catch {
							val = newValue;
						}
					}
				}
				acc[fieldName] = val;
				return acc;
		  }, {})
		: {};
};

export const cleanValues = (record, revisionInfo) => {
	const copy = JSON.parse(JSON.stringify(record));
	delete copy.approvedAt;
	delete copy.approvedBy;
	delete copy.disputedAt;
	delete copy.disputedBy;
	delete copy.proposalPdfLink;
	delete copy.invoicePdfLink;

	return { ...copy, ...revisionInfo };
};

export const getRecordRev = (record = {}, rev = 0) => {
	const revisionChanges = nullSafeGetOrElse(`revisionChanges`, record, []).find(
		(revChanges) => revChanges.revision === rev
	);
	if (revisionChanges) {
		return {
			...cleanValues(record, revisionChanges),
			...formatRevisionInfo(revisionChanges),
		};
	}
	return record;
};
export const recordHasRevision = (record = {}) =>
	record.hasOwnProperty('revision') || nullSafeGetOrElse('revisionChanges.length', record, 0) > 0;

type RevisionChange = {
	revision: number;
	revisedAt?: string;
	revisedByContact?: any;
	[key: string]: any;
};

export const getLatestRevisionChange = (record = {}): RevisionChange | null => {
	const changes: RevisionChange[] = nullSafeGetOrElse('revisionChanges', record, []);
	if (!changes.length) return null;
	return changes.slice().sort((left, right) => Number(right.revision) - Number(left.revision))[0];
};

export const getRevisedOrFallbackField = (
	record = {},
	revisedField: string,
	fallbackField: string
) => {
	const topLevelRevised = nullSafeGet(revisedField, record);
	if (topLevelRevised) return topLevelRevised;
	const latest = getLatestRevisionChange(record);
	if (latest && latest[revisedField]) return latest[revisedField];
	return nullSafeGet(fallbackField, record);
};

export const getInvoiceSubmittedContact = (invoice = {}) =>
	getRevisedOrFallbackField(invoice, 'revisedByContact', 'publishedByContact');
export const getInvoiceSubmittedAt = (invoice = {}) =>
	getRevisedOrFallbackField(invoice, 'revisedAt', 'publishedAt');

export const getLastApprovedOrDisputedRevision = (record) => {
	if (record && record.revisionChanges) {
		const revisions = record.revisionChanges;

		const sorted = revisions.slice().sort((a, b) => Number(b.revision) - Number(a.revision));
		const withoutCurrent = sorted.slice(1);

		const approvedOrDisputed = withoutCurrent.find((rev) => rev.approvedAt || rev.disputedAt);
		if (approvedOrDisputed) {
			return getRecordRev(record, approvedOrDisputed.revision); //If a version is approved or disputed, return it
		}

		return nullSafeGet('revision', withoutCurrent[0])
			? getRecordRev(record, nullSafeGet('revision', withoutCurrent[0])) //Otherwise return the one before this
			: undefined;
	}
	return undefined;
};
